// 3rd party
import * as React from 'react';
import * as debounce from 'lodash.debounce';
import { observer } from 'mobx-react';
import { Controller, useForm } from 'react-hook-form';

// Bernie
import { AnalyticsStore } from 'bernie-plugin-mobx';

// UI Toolkit
import { UitkPrimaryButton, UitkSecondaryButton } from 'uitk-react-button';
import { UitkIcon } from 'uitk-react-icons';
import { UitkInformativeInput, UitkTextArea, UitkSelect } from 'uitk-react-input';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkLayoutPosition } from 'uitk-react-layout-position';
import { UitkLink } from 'uitk-react-link';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading, UitkParagraph, UitkText } from 'uitk-react-text';
import { UitkLoader } from 'uitk-react-loader';

// Shared UI
import { LocalizedText, useLocalization } from '@shared-ui/localization-context';

// Models
import { ProductV2, Program } from 'models';

// Hooks
import { useStores } from 'hooks';
import { MoreInformationDialog, MoreInformationDialogTriggerButton } from 'components/shared/more-information';

// Enums
import { Image, Layout, Link, Message } from 'components/banners/enums';

// Utils
import {
  analyticsEvent,
  getProgramAnalyticsMicroMessage,
  getProductAnalyticsMicroMessage,
  getUserAnalyticsMicroMessage,
} from '../../util/analytics';

interface BannersV2Props {
  analytics?: AnalyticsStore;
  program?: Program;
  product?: ProductV2;
}

interface BannersV2State {
  pubref: string;
  bufferedPubref: string;
  layout: Layout;
  image: Image;
  message: Message;
  link: Link;
}

const BannersV2 = observer(
  // tslint:disable-next-line:no-shadowed-variable
  function BannersV2({ program, product }: BannersV2Props): JSX.Element {
    const { analytics, context, userAuth, user, pzStore } = useStores();
    const l10n = useLocalization();
    const { activeProgram } = user;

    const frameRef = React.useRef<HTMLIFrameElement>();

    const copyToClipboardLabel = l10n.formatText('product.widget.code.form.actions.copy_to_clipboard');
    const copiedToClipboardLabel = l10n.formatText('product.widget.code.form.actions.copied_to_clipboard');

    function pasteToClipboard(copyText) {
      navigator.clipboard.writeText(copyText);

      const copyButton = document.getElementById('code-copy');
      copyButton.textContent = copiedToClipboardLabel;
      setTimeout(() => {
        copyButton.textContent = copyToClipboardLabel;
      }, 1500);
    }

    const initialState: BannersV2State = {
      pubref: '',
      bufferedPubref: '',
      layout: Layout.MEDIUM_RECTANGLE,
      image: Image.SAILING,
      message: Message.BYE_BYE_BUCKET_LIST_HELLO_ADVENTURE,
      link: Link.HOME,
    };

    const [state, setState] = React.useState(initialState);
    const [showCodeMarkup, setShowCodeMarkup] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(true);

    const {
      control,
      formState: { errors },
      handleSubmit,
      reset,
      register,
      setValue,
    } = useForm<BannersV2State>({ mode: 'onBlur', defaultValues: initialState });

    const specialChars = ['>', '<', '&', "'", '"'];

    const analyticsTrackEvents = (rfrr: string) => {
      const microMessages = [
        getProgramAnalyticsMicroMessage(activeProgram, activeProgram?.programStatus),
        getProductAnalyticsMicroMessage(product),
      ];

      const filterAnalyticsTrackEvent = {
        'affhub.product_config': () => {
          analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Product Config Presented', microMessages);
        },
        'affhub_copy_code.selected': () => {
          analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Copy Code Selected', microMessages);
        },
        'affhub_code_create.selected': () => {
          analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Code Create Selected', microMessages);
        },
        'affhub_code_create.succeeded': () => {
          analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Code Create Succeeded', microMessages);
        },
        'affhub_code_create.failed': () => {
          analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Code Create Failed', microMessages);
        },
        'affhub_implementation_guide.selected': () => {
          analyticsEvent(analytics, userAuth, pzStore, rfrr, 'AffHub Implementation Guide Selected', microMessages);
        },
      };

      filterAnalyticsTrackEvent[rfrr]();
    };

    function onFormValid(formState: BannersV2State) {
      analyticsTrackEvents('affhub_code_create.succeeded');

      setState(formState);
      setShowCodeMarkup(true);
    }

    // tslint:disable-next-line:no-shadowed-variable
    function onFormInvalid() {
      // analytics
      analyticsTrackEvents('affhub_code_create.failed');
    }

    function handleMessage(event: MessageEvent) {
      if (event?.data.type === 'eg-affiliate-banners/resize') {
        const { current: frame } = frameRef;
        const { payload } = event.data;

        frame.style.height = payload.frame.style.height;
        frame.style.width = payload.frame.style.width;
      }
    }

    React.useEffect(() => {
      // Page load analytics
      analyticsTrackEvents('affhub.product_config');

      window.addEventListener('message', handleMessage);

      return function cleanup() {
        window.removeEventListener('message', handleMessage);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleFormReset(event) {
      event.preventDefault();

      reset(initialState);
      setState(initialState);
      setShowCodeMarkup(false);
      setIsLoading(true);
    }

    const setBufferedPubref = React.useCallback(
      // tslint:disable-next-line:no-shadowed-variable
      debounce(function setBufferedPubref(bufferedPubref: string) {
        setState((prevState) => ({
          ...prevState,
          bufferedPubref,
        }));
        setValue('bufferedPubref', bufferedPubref);
      }, 250),
      [],
    );

    function handlePubrefChange(pubref: string) {
      setState((prevState) => ({
        ...prevState,
        pubref,
      }));

      setBufferedPubref(pubref);
    }

    const onSubmit = handleSubmit(onFormValid, onFormInvalid);

    const codeMarkup =
      '<div' +
      ' class="eg-affiliate-banners"' +
      ` data-program="${activeProgram?.name}"` +
      ` data-camref="${activeProgram?.camRef}"` +
      ` data-pubref="${state.pubref}"` +
      ` data-layout="${state.layout}"` +
      ` data-image="${state.image}"` +
      ` data-message="${state.message}"` +
      ` data-link="${state.link}"` +
      '></div>\n' +
      '<script class="eg-affiliate-banners-script" src="https://affiliates.expediagroup.com/banners/assets/eg-affiliate-banners.js"></script>';

    function getIframeSrc(): string {
      return `${product.demoLink}&camref=${activeProgram?.camRef}&pubref=${state.bufferedPubref}&layout=${state.layout}&image=${state.image}&message=${state.message}&link=${state.link}`;
    }

    function renderPreviewFrame() {
      return (
        <>
          <link
            className="eg-affiliate-banners-style"
            rel="stylesheet"
            href="https://affiliates.expediagroup.com/banners/assets/eg-affiliate-banners.css"
          />
          <div
            className="eg-affiliate-banners"
            data-program={activeProgram?.name}
            data-camref={activeProgram?.camRef}
            data-pubref={state.bufferedPubref}
            data-layout={state.layout}
            data-image={state.image}
            data-message={state.message}
            data-link={state.link}
          >
            <iframe
              className="eg-affiliate-banners-frame eg-affiliate-banners-frame"
              ref={frameRef}
              src={getIframeSrc()}
              onLoad={() => {
                setIsLoading(false);
              }}
            />
          </div>
          {/*<script className="eg-affiliate-banners-script" src="https://affiliates.expediagroup.com/banners/assets/eg-affiliate-banners.js" />*/}
        </>
      );
    }

    return (
      <article>
        <UitkLayoutGrid columns={{ small: 1, large: 3 }} space="twelve">
          <UitkLayoutGridItem colSpan={{ small: 1, large: 1 }}>
            <div>
              <section>
                <form name={'banners'} onSubmit={onSubmit} onReset={handleFormReset} noValidate={true}>
                  <UitkLayoutGrid space="four">
                    <UitkLayoutGridItem>
                      <div>
                        <UitkLayoutGrid space="four">
                          <UitkLayoutGridItem>
                            <div>
                              <section>
                                <UitkSpacing margin={{ blockend: 'one' }}>
                                  <legend>
                                    <UitkHeading tag="h2" size={4}>
                                      <LocalizedText message="product.form.configuration.title" />
                                    </UitkHeading>
                                  </legend>
                                </UitkSpacing>
                              </section>
                            </div>
                          </UitkLayoutGridItem>

                          <UitkLayoutGridItem>
                            <div>
                              <Controller
                                control={control}
                                name="pubref"
                                rules={{
                                  validate: (pubref) => {
                                    if (new RegExp(`[${specialChars.join('')}]+`).test(pubref)) {
                                      return l10n.formatText(
                                        'product.form.error.special_chars_invalid',
                                        specialChars.join(' '),
                                      );
                                    }
                                  },
                                }}
                                render={({ field: { onChange, onBlur, value, name } }) => (
                                  <UitkInformativeInput
                                    id="tracking-pubref"
                                    name={name}
                                    label={l10n.formatText(
                                      'product.form.tracking-information.pz.label.pubref_optional',
                                    )}
                                    informativeTrigger={<MoreInformationDialogTriggerButton id="pz-pubref" />}
                                    a11yInformativeTrigger={l10n.formatText(
                                      'more-information.dialog.trigger.more_information',
                                    )}
                                    value={value}
                                    onChange={({ target: { value: newValue } }) => {
                                      onChange(newValue);
                                      handlePubrefChange(newValue);
                                      setShowCodeMarkup(false);
                                      setIsLoading(true);
                                    }}
                                    onBlur={onBlur}
                                    invalid={errors.pubref?.message}
                                  />
                                )}
                              />
                              <input type="hidden" {...register('bufferedPubref')} />

                              <MoreInformationDialog
                                id="pz-pubref"
                                title={l10n.formatText('more-information.dialog.pz-pubref.title.what_pubref')}
                                description={l10n.formatText(
                                  'more-information.dialog.pz-pubref.description.pubref_optional_reference_include_tracking_link_for_your_reporting_purposes_pubref_tracks_clicks_through_bookings',
                                )}
                              />
                            </div>
                          </UitkLayoutGridItem>

                          <UitkLayoutGridItem>
                            <div>
                              <Controller
                                control={control}
                                name="layout"
                                render={({ field: { onChange, onBlur, value, name } }) => (
                                  <UitkSelect
                                    id="layout"
                                    name={name}
                                    label={l10n.formatText('product.banners.configure.form.layout.label')}
                                    value={value}
                                    onChange={({ target: { value: newValue } }) => {
                                      onChange(newValue);
                                      setState((prevState) => ({
                                        ...prevState,
                                        layout: newValue as Layout,
                                      }));
                                      setShowCodeMarkup(false);
                                      setIsLoading(true);
                                    }}
                                    onBlur={onBlur}
                                  >
                                    <option value={Layout.MEDIUM_RECTANGLE}>
                                      {l10n.formatText('product.banners.configure.form.layout.label.medium-rectangle')}
                                    </option>
                                    <option value={Layout.LEADERBOARD}>
                                      {l10n.formatText('product.banners.configure.form.layout.label.leaderboard')}
                                    </option>
                                    <option value={Layout.WIDE_SKYSCRAPER}>
                                      {l10n.formatText('product.banners.configure.form.layout.label.wide-skyscraper')}
                                    </option>
                                  </UitkSelect>
                                )}
                              />
                            </div>
                          </UitkLayoutGridItem>

                          <UitkLayoutGridItem>
                            <div>
                              <Controller
                                control={control}
                                name="image"
                                render={({ field: { onChange, onBlur, value, name } }) => (
                                  <UitkSelect
                                    id="image"
                                    name={name}
                                    label={l10n.formatText('product.banners.configure.form.image.label')}
                                    value={value}
                                    onChange={({ target: { value: newValue } }) => {
                                      onChange(newValue);
                                      setState((prevState) => ({
                                        ...prevState,
                                        image: newValue as Image,
                                      }));
                                      setShowCodeMarkup(false);
                                      setIsLoading(true);
                                    }}
                                    onBlur={onBlur}
                                  >
                                    <option value={Image.ADVENTURE}>
                                      {l10n.formatText('product.banners.configure.form.image.label.adventure')}
                                    </option>
                                    <option value={Image.BEACH}>
                                      {l10n.formatText('product.banners.configure.form.image.label.beach')}
                                    </option>
                                    <option value={Image.CASTLE}>
                                      {l10n.formatText('product.banners.configure.form.image.label.castle')}
                                    </option>
                                    <option value={Image.CITY}>
                                      {l10n.formatText('product.banners.configure.form.image.label.city')}
                                    </option>
                                    <option value={Image.COASTAL}>
                                      {l10n.formatText('product.banners.configure.form.image.label.coastal')}
                                    </option>
                                    <option value={Image.COLORFUL_VILLAGE}>
                                      {l10n.formatText('product.banners.configure.form.image.label.colorful-village')}
                                    </option>
                                    <option value={Image.FALL}>
                                      {l10n.formatText('product.banners.configure.form.image.label.fall')}
                                    </option>
                                    {/*<option value={Image.FAMILY_TRIP}>*/}
                                    {/*  {l10n.formatText('product.banners.configure.form.image.label.family-trip')}*/}
                                    {/*</option>*/}
                                    {/*<option value={Image.HOTEL}>*/}
                                    {/*  {l10n.formatText('product.banners.configure.form.image.label.hotel')}*/}
                                    {/*</option>*/}
                                    <option value={Image.MOUNTAINS}>
                                      {l10n.formatText('product.banners.configure.form.image.label.mountains')}
                                    </option>
                                    <option value={Image.NATURE}>
                                      {l10n.formatText('product.banners.configure.form.image.label.nature')}
                                    </option>
                                    <option value={Image.PEACEFUL}>
                                      {l10n.formatText('product.banners.configure.form.image.label.peaceful')}
                                    </option>
                                    <option value={Image.RELAXING}>
                                      {l10n.formatText('product.banners.configure.form.image.label.relaxing')}
                                    </option>
                                    <option value={Image.RESORT}>
                                      {l10n.formatText('product.banners.configure.form.image.label.resort')}
                                    </option>
                                    <option value={Image.ROMANTIC}>
                                      {l10n.formatText('product.banners.configure.form.image.label.romantic')}
                                    </option>
                                    <option value={Image.SAILING}>
                                      {l10n.formatText('product.banners.configure.form.image.label.sailing')}
                                    </option>
                                    <option value={Image.SEARCHING}>
                                      {l10n.formatText('product.banners.configure.form.image.label.searching')}
                                    </option>
                                    {/*<option value={Image.SKI_SLOPE}>*/}
                                    {/*  {l10n.formatText('product.banners.configure.form.image.label.ski-slope')}*/}
                                    {/*</option>*/}
                                    <option value={Image.SOLO_TRIP}>
                                      {l10n.formatText('product.banners.configure.form.image.label.solo-trip')}
                                    </option>
                                    <option value={Image.SUNSET}>
                                      {l10n.formatText('product.banners.configure.form.image.label.sunset')}
                                    </option>
                                    <option value={Image.SURFING}>
                                      {l10n.formatText('product.banners.configure.form.image.label.surfing')}
                                    </option>
                                    <option value={Image.TROPICAL}>
                                      {l10n.formatText('product.banners.configure.form.image.label.tropical')}
                                    </option>
                                    <option value={Image.WINTER}>
                                      {l10n.formatText('product.banners.configure.form.image.label.winter')}
                                    </option>
                                  </UitkSelect>
                                )}
                              />
                            </div>
                          </UitkLayoutGridItem>

                          <UitkLayoutGridItem>
                            <div>
                              <Controller
                                control={control}
                                name="message"
                                render={({ field: { onChange, onBlur, value, name } }) => (
                                  <UitkSelect
                                    id="message"
                                    name={name}
                                    label={l10n.formatText('product.banners.configure.form.message.label')}
                                    value={value}
                                    onChange={({ target: { value: newValue } }) => {
                                      onChange(newValue);
                                      setState((prevState) => ({
                                        ...prevState,
                                        message: newValue as Message,
                                      }));
                                      setShowCodeMarkup(false);
                                      setIsLoading(true);
                                    }}
                                    onBlur={onBlur}
                                  >
                                    <option value={Message.NONE}>
                                      {l10n.formatText('product.banners.configure.form.message.label.none')}
                                    </option>
                                    <option value={Message.AHOY_DISCOVER_PERFECT_CRUISE}>
                                      {l10n.formatText(
                                        'product.banners.configure.form.message.label.ahoy-discover-perfect-cruise',
                                      )}
                                    </option>
                                    <option value={Message.BYE_BYE_BUCKET_LIST_HELLO_ADVENTURE}>
                                      {l10n.formatText(
                                        'product.banners.configure.form.message.label.bye-bye-bucket-list-hello-adventure',
                                      )}
                                    </option>
                                    <option value={Message.EXPLORE_WORLD_TRAVEL}>
                                      {l10n.formatText(
                                        'product.banners.configure.form.message.label.explore-world-travel',
                                      )}
                                    </option>
                                    <option value={Message.FIND_PERFECT_CAR_RENTAL}>
                                      {l10n.formatText(
                                        'product.banners.configure.form.message.label.find-perfect-car-rental',
                                      )}
                                    </option>
                                    <option value={Message.FIND_PERFECT_GETAWAY_PACKAGE}>
                                      {l10n.formatText(
                                        'product.banners.configure.form.message.label.find-perfect-getaway-package',
                                      )}
                                    </option>
                                    <option value={Message.HOTEL_TREEHOUSE_FIND_PERFECT_PLACE_STAY}>
                                      {l10n.formatText(
                                        'product.banners.configure.form.message.label.hotel-treehouse-find-perfect-place-stay',
                                      )}
                                    </option>
                                    <option value={Message.MAKE_EXCITING_ACTIVITIES_ATTRACTIONS}>
                                      {l10n.formatText(
                                        'product.banners.configure.form.message.label.make-exciting-activities-attractions',
                                      )}
                                    </option>
                                    <option value={Message.READY_TAKEOFF_FIND_PERFECT_FLIGHT}>
                                      {l10n.formatText(
                                        'product.banners.configure.form.message.label.ready-takeoff-find-perfect-flight',
                                      )}
                                    </option>
                                    <option value={Message.SNOW_DAYS_FIND_ULTIMATE_SKI_GETAWAY}>
                                      {l10n.formatText(
                                        'product.banners.configure.form.message.label.snow-days-find-ultimate-ski-getaway',
                                      )}
                                    </option>
                                    <option value={Message.SEARCH_HOTELS_FLIGHTS_CARS_ACTIVITIES_MORE}>
                                      {l10n.formatText(
                                        'product.banners.configure.form.message.label.search-hotels-flights-cars-activities-more',
                                      )}
                                    </option>
                                    <option value={Message.START_TRAVEL_PLANNING}>
                                      {l10n.formatText(
                                        'product.banners.configure.form.message.label.start-travel-planning',
                                      )}
                                    </option>
                                    <option value={Message.SUN_SEEKER_FIND_PERFECT_BEACH_GETAWAY}>
                                      {l10n.formatText(
                                        'product.banners.configure.form.message.label.sun-seeker-find-perfect-beach-getaway',
                                      )}
                                    </option>
                                  </UitkSelect>
                                )}
                              />
                            </div>
                          </UitkLayoutGridItem>

                          <UitkLayoutGridItem>
                            <div>
                              <Controller
                                control={control}
                                name="link"
                                render={({ field: { onChange, onBlur, value, name } }) => (
                                  <UitkSelect
                                    id="link"
                                    name={name}
                                    label={l10n.formatText('product.banners.configure.form.link.label')}
                                    value={value}
                                    onChange={({ target: { value: newValue } }) => {
                                      onChange(newValue);
                                      setState((prevState) => ({
                                        ...prevState,
                                        link: newValue as Link,
                                      }));
                                      setShowCodeMarkup(false);
                                      setIsLoading(true);
                                    }}
                                    onBlur={onBlur}
                                  >
                                    <option value={Link.HOME}>
                                      {l10n.formatText('product.banners.configure.form.link.label.home')}
                                    </option>
                                    <option value={Link.STAYS}>
                                      {l10n.formatText('product.banners.configure.form.link.label.stays')}
                                    </option>
                                    <option value={Link.FLIGHTS}>
                                      {l10n.formatText('product.banners.configure.form.link.label.flights')}
                                    </option>
                                    <option value={Link.PACKAGES}>
                                      {l10n.formatText('product.banners.configure.form.link.label.packages')}
                                    </option>
                                    <option value={Link.CARS}>
                                      {l10n.formatText('product.banners.configure.form.link.label.cars')}
                                    </option>
                                    <option value={Link.ACTIVITIES}>
                                      {l10n.formatText('product.banners.configure.form.link.label.activities')}
                                    </option>
                                    <option value={Link.CRUISES}>
                                      {l10n.formatText('product.banners.configure.form.link.label.cruises')}
                                    </option>
                                  </UitkSelect>
                                )}
                              />
                            </div>
                          </UitkLayoutGridItem>

                          <UitkLayoutFlex direction="row" space="four">
                            <UitkLayoutFlexItem>
                              <div>
                                <UitkPrimaryButton
                                  type="submit"
                                  onClick={() => {
                                    analyticsTrackEvents('affhub_code_create.selected');
                                  }}
                                >
                                  <LocalizedText message="product.form.actions.generate_code" />
                                </UitkPrimaryButton>
                              </div>
                            </UitkLayoutFlexItem>

                            <UitkLayoutFlexItem>
                              <div>
                                <UitkSecondaryButton type="reset">
                                  <LocalizedText message="product.form.actions.reset" />
                                </UitkSecondaryButton>
                              </div>
                            </UitkLayoutFlexItem>
                          </UitkLayoutFlex>
                        </UitkLayoutGrid>
                      </div>
                    </UitkLayoutGridItem>

                    <UitkLayoutGridItem>
                      <div>
                        {showCodeMarkup && (
                          <section id={codeMarkup}>
                            <UitkParagraph size={1}>
                              <LocalizedText message="product.widget.code.instructions.to_embed_this_copy_code_read_guide" />
                            </UitkParagraph>

                            <UitkParagraph size={1}>
                              <UitkLink
                                inline={true}
                                onClick={() => {
                                  analyticsTrackEvents('affhub_implementation_guide.selected');
                                }}
                              >
                                <a href={product.guideLink} target="_blank" rel="noreferrer">
                                  <LocalizedText message="product.widget.code.implementation_guide" />
                                  <UitkIcon name="open_in_new" />
                                </a>
                              </UitkLink>
                            </UitkParagraph>

                            <UitkSpacing margin={{ blockstart: 'four' }}>
                              <form>
                                <UitkLayoutGrid space="two">
                                  <UitkLayoutGridItem>
                                    <div>
                                      <fieldset>
                                        {/* #widgetsCodeArea */}
                                        <UitkTextArea
                                          key={getIframeSrc()}
                                          id="code-markup"
                                          label={l10n.formatText('product.widget.code.form.markup.label')}
                                          value={codeMarkup}
                                          hasVisibleLabel={false}
                                        />
                                      </fieldset>
                                    </div>
                                  </UitkLayoutGridItem>

                                  <UitkLayoutGridItem>
                                    <div>
                                      <fieldset>
                                        <UitkSecondaryButton
                                          id="code-copy"
                                          onClick={() => {
                                            const textArea = document.getElementById(
                                              'code-markup',
                                            ) as HTMLTextAreaElement;
                                            textArea.focus();
                                            pasteToClipboard(textArea.value);
                                            analyticsTrackEvents('affhub_copy_code.selected');
                                          }}
                                        >
                                          {copyToClipboardLabel}
                                        </UitkSecondaryButton>
                                      </fieldset>
                                    </div>
                                  </UitkLayoutGridItem>
                                </UitkLayoutGrid>
                              </form>
                            </UitkSpacing>
                          </section>
                        )}
                      </div>
                    </UitkLayoutGridItem>
                  </UitkLayoutGrid>
                </form>
              </section>
            </div>
          </UitkLayoutGridItem>

          <UitkLayoutGridItem colSpan={{ small: 1, large: 2 }}>
            <UitkLayoutPosition type="relative">
              <UitkLayoutFlex className="Banners" direction="column" justifyContent="center" alignItems="center">
                <UitkLayoutFlexItem>
                  <section className="BannersFlex">
                    <div>
                      <div key={getIframeSrc()}>
                        {renderPreviewFrame()}
                        {isLoading && (
                          <UitkLayoutPosition
                            type="absolute"
                            position={{ top: 'center', left: 'center' }}
                            pointerEvents="none"
                          >
                            <UitkLoader isSmall={true} />
                          </UitkLayoutPosition>
                        )}
                      </div>
                    </div>
                  </section>
                </UitkLayoutFlexItem>
              </UitkLayoutFlex>
            </UitkLayoutPosition>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
      </article>
    );
  },
);

export default BannersV2;
export { BannersV2 };
